import React, { useState } from 'react';
import { Image as ImageIc, Video as VideoIc, FileText, Trash2, Loader2, ArrowUp, ArrowDown, Plus } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u);
const uid = () => Math.random().toString(36).slice(2, 12);

/**
 * Ordered content sections editor for posts.
 *
 * A section is one of:
 *   - { id, type: 'text',  textAr, textEn }
 *   - { id, type: 'image', url|file, captionAr, captionEn }
 *   - { id, type: 'video', url|file, captionAr, captionEn }
 *
 * Modes:
 *   - Server mode (postId set): changes persist immediately via the API.
 *   - Local mode  (postId null): sections are kept in memory; parent uploads
 *     them after creating the post, in order.
 *
 * Shape of `sections` is always an array of plain objects. For local mode,
 * image/video sections carry a `file: File` field instead of a `url`.
 */
const PostSectionsEditor = ({ postId, sections = [], onChange }) => {
  const { lang, isRTL } = useLang();
  const { toast } = useToast();
  const L = (ar, en) => tx(lang, ar, en);
  const isLocal = !postId;
  const [busy, setBusy] = useState(false);

  const emit = (next) => onChange?.(next);

  // Add a new section (text or with a file picker)
  const addText = async () => {
    const newSec = { id: uid(), type: 'text', textAr: '', textEn: '' };
    if (isLocal) {
      emit([...sections, newSec]);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('type', 'text');
      fd.append('order', String(sections.length));
      const { data } = await api.post(`/posts/${postId}/sections`, fd);
      emit(data.sections || []);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const addMedia = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (kind === 'image' && !file.type.startsWith('image/')) return;
    if (kind === 'video' && !file.type.startsWith('video/')) return;

    if (isLocal) {
      emit([...sections, { id: uid(), type: kind, file, captionAr: '', captionEn: '' }]);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('type', kind);
      fd.append('order', String(sections.length));
      fd.append('file', file);
      const { data } = await api.post(`/posts/${postId}/sections`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      emit(data.sections || []);
      toast({ title: L(`تم رفع ${kind === 'image' ? 'الصورة' : 'الفيديو'}`, `${kind} uploaded`) });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Upload failed' });
    } finally { setBusy(false); }
  };

  // Update a field on a section. For server mode, persist on blur.
  const updateField = (id, field, value) => {
    emit(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const persistField = async (section) => {
    if (isLocal) return;
    try {
      const fd = new FormData();
      ['textAr', 'textEn', 'captionAr', 'captionEn'].forEach(k => fd.append(k, section[k] || ''));
      await api.patch(`/posts/${postId}/sections/${section.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm(L('حذف هذا القسم؟', 'Delete this section?'))) return;
    if (isLocal) {
      emit(sections.filter(s => s.id !== id));
      return;
    }
    try {
      await api.delete(`/posts/${postId}/sections/${id}`);
      emit(sections.filter(s => s.id !== id));
      toast({ title: L('تم الحذف', 'Deleted') });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed' });
    }
  };

  const move = async (id, delta) => {
    const idx = sections.findIndex(s => s.id === id);
    const newIdx = idx + delta;
    if (idx < 0 || newIdx < 0 || newIdx >= sections.length) return;
    const next = [...sections];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    emit(next);
    if (isLocal) return;
    try {
      const fd = new FormData();
      fd.append('order', String(newIdx));
      await api.patch(`/posts/${postId}/sections/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      toast({ title: 'Error', description: 'Reorder failed' });
    }
  };

  const previewUrl = (s) => {
    if (s.url) return absUrl(s.url);
    if (s.file) return URL.createObjectURL(s.file);
    return '';
  };

  return (
    <div className="space-y-4 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="post-sections-editor">
      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
        <FileText className="w-5 h-5 text-blue-600" />
        <h4 className="text-slate-900 font-bold text-sm">{L('أقسام المحتوى (بالترتيب)', 'Content sections (ordered)')}</h4>
        <span className="text-xs text-slate-500">
          {L(`${sections.length} قسم`, `${sections.length} section(s)`)}
        </span>
      </div>
      <p className={`text-[11px] text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}>
        {L('أضف نصوصاً وصوراً وفيديوهات بالترتيب الذي تريده. يمكنك تكرار كل نوع عدة مرات.',
           'Add text, images and videos in any order. You can repeat each type as many times as you want.')}
      </p>

      {/* Sections list */}
      {sections.length > 0 && (
        <div className="space-y-3">
          {sections.map((s, i) => (
            <div key={s.id} className="rounded-lg bg-white border border-slate-200 p-3" data-testid={`section-item-${i}`}>
              <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[11px] font-bold uppercase">
                  {s.type === 'text' ? L('نص', 'Text') : s.type === 'image' ? L('صورة', 'Image') : L('فيديو', 'Video')}
                </span>
                <span className="text-slate-500 text-[11px]">#{i + 1}</span>
                <div className="flex-1" />
                <Button type="button" variant="outline" size="sm" onClick={() => move(s.id, -1)} disabled={i === 0} className="h-7 w-7 p-0 border-blue-200 text-blue-700 disabled:opacity-30" data-testid={`section-up-${i}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => move(s.id, 1)} disabled={i === sections.length - 1} className="h-7 w-7 p-0 border-blue-200 text-blue-700 disabled:opacity-30" data-testid={`section-down-${i}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => remove(s.id)} className="h-7 w-7 p-0 border-red-500/40 text-red-600" data-testid={`section-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              {s.type === 'text' && (
                <div className="grid sm:grid-cols-2 gap-2">
                  <Textarea
                    value={s.textAr || ''}
                    onChange={(e) => updateField(s.id, 'textAr', e.target.value)}
                    onBlur={() => persistField(s)}
                    placeholder={L('النص بالعربية', 'Text in Arabic')}
                    rows={3}
                    dir="rtl"
                    className="bg-slate-50 border-slate-200 text-slate-900 text-sm"
                    data-testid={`section-${i}-text-ar`}
                  />
                  <Textarea
                    value={s.textEn || ''}
                    onChange={(e) => updateField(s.id, 'textEn', e.target.value)}
                    onBlur={() => persistField(s)}
                    placeholder={L('النص بالإنجليزية', 'Text in English')}
                    rows={3}
                    dir="ltr"
                    className="bg-slate-50 border-slate-200 text-slate-900 text-sm"
                    data-testid={`section-${i}-text-en`}
                  />
                </div>
              )}

              {(s.type === 'image' || s.type === 'video') && (
                <div className="space-y-2">
                  <div className="rounded-md overflow-hidden bg-black border border-slate-200">
                    {s.type === 'image' ? (
                      <img src={previewUrl(s)} alt="" className="w-full max-h-64 object-cover" />
                    ) : s.url ? (
                      <video src={previewUrl(s)} controls preload="metadata" className="w-full max-h-72" />
                    ) : (
                      <div className="aspect-video flex items-center justify-center text-slate-500 text-xs">
                        <div className="text-center">
                          <VideoIc className="w-8 h-8 mx-auto mb-1 text-blue-600" />
                          <div className="truncate max-w-[220px]">{s.file?.name}</div>
                          <div className="text-[11px] text-amber-600 mt-1">{L('سيُرفع عند النشر', 'Will upload on publish')}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input
                      value={s.captionAr || ''}
                      onChange={(e) => updateField(s.id, 'captionAr', e.target.value)}
                      onBlur={() => persistField(s)}
                      placeholder={L('وصف بالعربية (اختياري)', 'Caption in Arabic (optional)')}
                      dir="rtl"
                      className="bg-slate-50 border-slate-200 text-slate-900 h-9 text-xs"
                      data-testid={`section-${i}-caption-ar`}
                    />
                    <Input
                      value={s.captionEn || ''}
                      onChange={(e) => updateField(s.id, 'captionEn', e.target.value)}
                      onBlur={() => persistField(s)}
                      placeholder={L('وصف بالإنجليزية (اختياري)', 'Caption in English (optional)')}
                      dir="ltr"
                      className="bg-slate-50 border-slate-200 text-slate-900 h-9 text-xs"
                      data-testid={`section-${i}-caption-en`}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add buttons */}
      <div className={`flex flex-wrap gap-2 pt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button type="button" onClick={addText} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white h-9" data-testid="section-add-text">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin me-1.5" /> : <Plus className="w-3.5 h-3.5 me-1.5" />}
          <FileText className="w-3.5 h-3.5 me-1.5" />
          {L('إضافة نص', 'Add text')}
        </Button>
        <label className="inline-flex">
          <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium cursor-pointer" data-testid="section-add-image-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            <ImageIc className="w-3.5 h-3.5" />
            {L('إضافة صورة', 'Add image')}
            <input type="file" accept="image/*" onChange={(e) => addMedia(e, 'image')} className="hidden" data-testid="section-add-image-input" />
          </span>
        </label>
        <label className="inline-flex">
          <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium cursor-pointer" data-testid="section-add-video-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            <VideoIc className="w-3.5 h-3.5" />
            {L('إضافة فيديو', 'Add video')}
            <input type="file" accept="video/*" onChange={(e) => addMedia(e, 'video')} className="hidden" data-testid="section-add-video-input" />
          </span>
        </label>
      </div>
    </div>
  );
};

export default PostSectionsEditor;
